'use client';

import { useMemo } from 'react';
import { useRegional } from '@/components/providers/regional-provider';
import { type CurrencyOption } from '@impact/types';
import { usePaymentConfig } from './usePaymentConfig';
import { CURRENCY_OPTIONS } from '../lib/currencies';

/**
 * Returns the CurrencyOption matching the user's regional currency selection.
 * Uses the canonical CURRENCY_CODES list from currencies.ts.
 */
export function useDisplayCurrency(): CurrencyOption {
  const { currency: selectedCurrency } = useRegional();

  return useMemo(() => {
    return CURRENCY_OPTIONS.find((c) => c.code === selectedCurrency) || CURRENCY_OPTIONS[0];
  }, [selectedCurrency]);
}

/**
 * Returns the actual Adyen processing currency for the DonationForm.
 * Uses usePaymentConfig when an eventId is provided, falls back to useDisplayCurrency.
 */
export function useDonationCurrency(eventId?: string): CurrencyOption {
  const displayCurrency = useDisplayCurrency();
  const { data: paymentConfig } = usePaymentConfig(eventId);

  return useMemo(() => {
    const pc = paymentConfig?.paymentConfigs?.[0];
    if (pc?.currencyCode && pc.currencySymbol && pc.currencyName) {
      return {
        code: pc.currencyCode,
        symbol: pc.currencySymbol,
        label: pc.currencyName,
      };
    }
    return displayCurrency;
  }, [paymentConfig, displayCurrency]);
}
